/**
 * Przeniesienie kadry z innego programu (Etap 55, ADR-0061).
 *
 * Trzy pliki CSV i miesiąc przejścia. Serwer sprawdza pliki (podgląd), a zapis
 * idzie dopiero przy komplecie bez błędów — w całości albo wcale. Błąd stoi pod
 * nazwą kolumny ze wzoru pliku, bo tę nazwę kadry widzą w swoim arkuszu.
 */
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, FormField, InfoBanner, Input, Modal, Spinner } from '@k1p4/ui'
import {
  MIGRATION_HEADERS,
  staffMigrationTemplates,
  type MigrationFileError,
  type MigrationFileParsed,
} from '@k1p4/shared'
import { ApiError, api, type MigrationAnalysis, type MigrationBody, type MigrationResult } from '../../api/client'
import { useFormat } from '../../format'
import styles from './StaffMigrationModal.module.css'

export interface StaffMigrationModalProps {
  open: boolean
  onClose: () => void
  onChanged: () => void
}

type FileKey = 'employees' | 'history' | 'sickLeaves'
const FILE_KEYS: readonly FileKey[] = ['employees', 'history', 'sickLeaves']
const FILE_TITLE: Record<FileKey, string> = {
  employees: 'staffMigration.fileEmployeesTitle',
  history: 'staffMigration.fileHistoryTitle',
  sickLeaves: 'staffMigration.fileSickLeavesTitle',
}
const FILE_LABEL: Record<FileKey, string> = {
  employees: 'staffMigration.fileEmployees',
  history: 'staffMigration.fileHistory',
  sickLeaves: 'staffMigration.fileSickLeaves',
}
const TEMPLATE_LABEL: Record<FileKey, string> = {
  employees: 'staffMigration.templateEmployees',
  history: 'staffMigration.templateHistory',
  sickLeaves: 'staffMigration.templateSickLeaves',
}
const TEMPLATE_NAME: Record<FileKey, string> = {
  employees: 'pracownicy.csv',
  history: 'historia-plac.csv',
  sickLeaves: 'zwolnienia.csv',
}

function download(name: string, text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }))
  try {
    const link = document.createElement('a')
    link.href = url
    link.download = name
    link.click()
  } finally {
    URL.revokeObjectURL(url)
  }
}

export function StaffMigrationModal({ open, onClose, onChanged }: StaffMigrationModalProps) {
  const { t } = useTranslation()
  const fmt = useFormat()
  const queryClient = useQueryClient()
  const ids = { cutover: useId(), employees: useId(), history: useId(), sickLeaves: useId() }
  const [cutover, setCutover] = useState('')
  const [files, setFiles] = useState<Partial<Record<FileKey, string>>>({})
  const [analysis, setAnalysis] = useState<MigrationAnalysis | null>(null)
  const [result, setResult] = useState<MigrationResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const state = useQuery({ queryKey: ['migration'], queryFn: () => api.migrationState(), enabled: open })

  const body = (): MigrationBody | null =>
    files.employees === undefined || !/^\d{4}-\d{2}$/.test(cutover)
      ? null
      : {
          cutoverMonth: cutover,
          employees: files.employees,
          ...(files.history !== undefined ? { history: files.history } : {}),
          ...(files.sickLeaves !== undefined ? { sickLeaves: files.sickLeaves } : {}),
        }

  const check = useMutation({
    mutationFn: (payload: MigrationBody) => api.previewMigration(payload),
    onSuccess: (data) => setAnalysis(data),
    onError: () => setError('staffMigration.errorGeneric'),
  })
  const save = useMutation({
    mutationFn: (payload: MigrationBody) => api.commitMigration(payload),
    onSuccess: (data) => {
      setResult(data)
      void queryClient.invalidateQueries({ queryKey: ['migration'] })
      onChanged()
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError && cause.code === 'MIGRATION_CONFLICT'
          ? 'staffMigration.errorConflict'
          : 'staffMigration.errorGeneric',
      )
    },
  })
  const revert = useMutation({
    mutationFn: () => api.revertMigration(),
    onSuccess: () => {
      setNotice('staffMigration.reverted')
      void queryClient.invalidateQueries({ queryKey: ['migration'] })
      onChanged()
    },
    onError: () => setError('staffMigration.locked'),
  })

  function close(): void {
    setCutover('')
    setFiles({})
    setAnalysis(null)
    setResult(null)
    setError(null)
    setNotice(null)
    onClose()
  }

  function fileErrorText(fileError: MigrationFileError): string {
    if (fileError.code === 'missingColumns') {
      return t('staffMigration.fileErrors.missingColumns', { columns: fileError.columns.join(', ') })
    }
    if (fileError.code === 'tooManyRows') return t('staffMigration.fileErrors.tooManyRows', { max: fileError.max })
    if (fileError.code === 'unclosedQuote') return t('staffMigration.fileErrors.unclosedQuote', { line: fileError.line })
    return t(`staffMigration.fileErrors.${fileError.code}`)
  }

  function renderFile(key: FileKey, parsed: MigrationFileParsed<unknown> | null) {
    if (parsed === null) return null
    const headers = MIGRATION_HEADERS[key] as Record<string, string>
    const problems = parsed.rows.filter((row) => row.errors.length > 0 || row.warnings.length > 0)
    return (
      <section key={key} className={styles.file}>
        <h3 className={styles.fileTitle}>{t(FILE_TITLE[key])}</h3>
        {parsed.fileErrors.map((fileError) => (
          <InfoBanner key={fileError.code} tone="danger" className={styles.banner}>
            {fileErrorText(fileError)}
          </InfoBanner>
        ))}
        {parsed.ignoredColumns.length > 0 ? (
          <p className={styles.note}>{t('staffMigration.ignored', { columns: parsed.ignoredColumns.join(', ') })}</p>
        ) : null}
        {problems.length > 0 ? (
          <ul className={styles.list}>
            {problems.map((row) => (
              <li key={row.line} className={styles.row}>
                <span className={styles.rowLine}>{t('staffMigration.line', { line: row.line })}</span>
                {row.errors.length > 0 ? (
                  <span className={styles.rowError}>
                    {row.errors
                      .map((item) => `${headers[item.field] ?? item.field}: ${t(`staffMigration.errors.${item.code}`)}`)
                      .join(' · ')}
                  </span>
                ) : null}
                {row.warnings.length > 0 ? (
                  <span className={styles.rowWarning}>
                    {row.warnings
                      .map((item) => `${headers[item.field] ?? item.field}: ${t(`staffMigration.warningCodes.${item.code}`)}`)
                      .join(' · ')}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}
      </section>
    )
  }

  const payload = body()
  const migrated = state.data?.migratedPeople ?? 0

  return (
    <Modal
      open={open}
      onClose={close}
      title={t('staffMigration.title')}
      footer={
        result ? (
          <Button onClick={close}>{t('staffMigration.close')}</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={close}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="secondary"
              disabled={payload === null || check.isPending}
              onClick={() => {
                setError(null)
                if (payload) check.mutate(payload)
              }}
            >
              {check.isPending ? <Spinner label={t('staffMigration.checking')} /> : t('staffMigration.check')}
            </Button>
            <Button
              disabled={analysis?.valid !== true || payload === null || save.isPending}
              onClick={() => {
                setError(null)
                if (payload) save.mutate(payload)
              }}
            >
              {save.isPending ? (
                <Spinner label={t('staffMigration.sending')} />
              ) : analysis?.valid === true ? (
                t('staffMigration.submit', { count: analysis.summary.people })
              ) : (
                t('staffMigration.submitIdle')
              )}
            </Button>
          </>
        )
      }
    >
      {result ? (
        <div className={styles.result}>
          <InfoBanner tone="info">
            {t('staffMigration.done', { people: result.people, history: result.historyPeriods, sick: result.sickLeaves })}
          </InfoBanner>
          {result.notMailed.length > 0 ? (
            <>
              <p className={styles.lead}>{t('staffMigration.notMailed', { url: result.signInUrl })}</p>
              <ul className={styles.list}>
                {result.notMailed.map((item) => (
                  <li key={item.line} className={styles.row}>
                    {item.email}
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </div>
      ) : (
        <>
          <p className={styles.lead}>{t('staffMigration.lead')}</p>

          {migrated > 0 && state.data?.cutoverMonth ? (
            <div className={styles.state}>
              <span>
                {t('staffMigration.state', { month: fmt.month(state.data.cutoverMonth, 'period'), people: migrated })}
              </span>
              {state.data.locked ? (
                <span className={styles.note}>{t('staffMigration.locked')}</span>
              ) : (
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={revert.isPending}
                  onClick={() => {
                    if (globalThis.confirm(t('staffMigration.revertConfirm'))) revert.mutate()
                  }}
                >
                  {t('staffMigration.revert')}
                </Button>
              )}
            </div>
          ) : null}
          {notice !== null ? (
            <InfoBanner tone="info" className={styles.banner}>
              {t(notice)}
            </InfoBanner>
          ) : null}

          <FormField label={t('staffMigration.cutover')} htmlFor={ids.cutover} hint={t('staffMigration.cutoverHint')}>
            <Input
              id={ids.cutover}
              type="month"
              value={cutover}
              onChange={(event) => {
                setCutover(event.target.value)
                setAnalysis(null)
              }}
            />
          </FormField>

          <p className={styles.templatesTitle}>{t('staffMigration.templates')}</p>
          <div className={styles.templates}>
            {FILE_KEYS.map((key) => (
              <Button
                key={key}
                size="sm"
                variant="secondary"
                onClick={() => download(TEMPLATE_NAME[key], staffMigrationTemplates()[key])}
              >
                {t(TEMPLATE_LABEL[key])}
              </Button>
            ))}
          </div>

          {FILE_KEYS.map((key) => (
            <FormField
              key={key}
              label={t(FILE_LABEL[key])}
              htmlFor={ids[key]}
              {...(key === 'employees' ? { hint: t('staffMigration.fileHint') } : {})}
            >
              <input
                id={ids[key]}
                className={styles.input}
                type="file"
                accept=".csv,text/csv"
                onChange={(event) => {
                  const file = event.target.files?.[0]
                  setAnalysis(null)
                  setError(null)
                  if (!file) {
                    setFiles((current) => {
                      const next = { ...current }
                      delete next[key]
                      return next
                    })
                    return
                  }
                  void file.text().then((text) => setFiles((current) => ({ ...current, [key]: text })))
                }}
              />
            </FormField>
          ))}

          {error !== null ? (
            <InfoBanner tone="danger" className={styles.banner}>
              {t(error)}
            </InfoBanner>
          ) : null}

          {analysis ? (
            <div className={styles.preview}>
              <p className={styles.summary}>
                {t('staffMigration.summary', {
                  people: analysis.summary.people,
                  history: analysis.summary.historyMonths,
                  sick: analysis.summary.sickLeaves,
                })}
              </p>
              {analysis.blockers.map((blocker) => (
                <InfoBanner key={blocker.code} tone="danger" className={styles.banner}>
                  {t(`staffMigration.blockers.${blocker.code}`, {
                    month: blocker.cutoverMonth ? fmt.month(blocker.cutoverMonth, 'period') : '',
                  })}
                </InfoBanner>
              ))}
              <InfoBanner tone={analysis.valid ? 'info' : 'warning'} className={styles.banner}>
                {t(analysis.valid ? 'staffMigration.ready' : 'staffMigration.blocked')}
              </InfoBanner>
              {analysis.summary.warnings > 0 ? (
                <p className={styles.note}>{t('staffMigration.warnings', { count: analysis.summary.warnings })}</p>
              ) : null}
              {renderFile('employees', analysis.employees)}
              {renderFile('history', analysis.history)}
              {renderFile('sickLeaves', analysis.sickLeaves)}
            </div>
          ) : null}
        </>
      )}
    </Modal>
  )
}
